import { DAILY_GRANT, GAME_IDS, type GameId } from '@casino/shared';
import { ApiError, apiCall } from '../api/client.js';
import { audio } from '../audio/AudioManager.js';
import { applyUserState, dailyRemainingMs, store } from '../state/store.js';
import { animate, DURATION, EASING, enter, formatChips, formatDuration, isReducedMotion } from './anim.js';
import { el, icon, ICONS } from './dom.js';
import { gameEmblem } from './gameEmblems.js';
import { openFairness } from './fairness.js';
import { createHud, type Hud } from './hud.js';
import { openSettings } from './settings.js';
import { toast } from './toast.js';

/**
 * Menu principal : bandeau joueur, table des jeux, bonus quotidien.
 *
 * Seules les tables reellement montees sont cliquables. Les autres restent
 * annoncees « Bientôt » et desactivees : on ne simule pas une table absente.
 */

interface GameCard {
  readonly id: GameId;
  readonly name: string;
  readonly tagline: string;
  /** Motif decoratif de la carte, purement CSS. */
  readonly motif: 'reels' | 'cards' | 'wheel' | 'chips';
  /** `true` quand l'ecran du jeu existe et que le serveur expose ses routes. */
  readonly open: boolean;
}

const GAMES: Readonly<Record<GameId, GameCard>> = {
  slots: {
    id: 'slots',
    name: 'Machines à sous',
    tagline: 'Trois rouleaux, une ligne, aucune promesse.',
    motif: 'reels',
    open: true,
  },
  blackjack: {
    id: 'blackjack',
    name: 'Blackjack',
    tagline: 'Tirer ou rester. Le croupier ne bluffe pas.',
    motif: 'cards',
    open: true,
  },
  roulette: {
    id: 'roulette',
    name: 'Roulette',
    tagline: 'Trente-sept cases, une bille, rien d’autre.',
    motif: 'wheel',
    open: true,
  },
  poker: {
    id: 'poker',
    name: 'Poker',
    tagline: 'Texas Hold’em. Vos amis, pas la maison.',
    motif: 'chips',
    open: false,
  },
};

export interface MenuOptions {
  /** Ouvre une table. N'est appele que pour les jeux marques `open`. */
  readonly onOpenGame?: (id: GameId) => void;
}

export interface MenuScreen {
  readonly root: HTMLElement;
  readonly destroy: () => void;
}

function gameCardNode(card: GameCard, index: number, options: MenuOptions): HTMLElement {
  const open = card.open && options.onOpenGame !== undefined;

  const node = el(
    open
      ? 'button'
      : 'article',
    {
      class: 'game-card',
      data: { motif: card.motif, state: open ? 'open' : 'soon' },
      attrs: open
        ? { type: 'button', 'aria-label': `${card.name} — ouvrir la table` }
        : { 'aria-disabled': 'true' },
    },
    [
      el('div', { class: 'game-card__motif', attrs: { 'aria-hidden': 'true' } }, [
        gameEmblem(card.id),
      ]),
      el('div', { class: 'game-card__text' }, [
        el('h3', { class: 'game-card__name', text: card.name }),
        el('p', { class: 'game-card__tagline', text: card.tagline }),
      ]),
      el('span', { class: 'game-card__badge', text: open ? 'Ouvert' : 'Bientôt' }),
    ],
  );

  node.addEventListener('click', () => {
    if (open) {
      audio.play('click');
      options.onOpenGame?.(card.id);
      return;
    }
    // Table fermee : on le dit clairement plutot que de laisser croire a un bug.
    toast(`${card.name} n’ouvre pas encore.`, { kind: 'info', sound: false });
    audio.play('error');
    if (!isReducedMotion()) {
      animate(node, [{ transform: 'scale(1)' }, { transform: 'scale(0.985)' }, { transform: 'scale(1)' }], {
        duration: DURATION.micro,
        easing: EASING.inOut,
      });
    }
  });

  enter(node, index + 2);
  return node;
}

export function createMenu(options: MenuOptions = {}): MenuScreen {
  const hud: Hud = createHud();

  // --- bonus quotidien ----------------------------------------------------
  const dailyLabel = el('span', { class: 'daily__label', text: 'Bonus quotidien' });
  const dailySub = el('span', { class: 'daily__sub', text: `+${formatChips(DAILY_GRANT)} jetons` });
  const dailyButton = el(
    'button',
    { class: 'daily', attrs: { type: 'button' } },
    [icon(ICONS.gift, { size: 22 }), el('span', { class: 'daily__text' }, [dailyLabel, dailySub])],
  );

  let claiming = false;

  function refreshDaily(): void {
    if (claiming) return;
    const remaining = dailyRemainingMs();
    const available = remaining === 0 && store.state.user !== null;
    dailyButton.disabled = !available;
    dailyButton.dataset['state'] = available ? 'ready' : 'waiting';
    dailyLabel.textContent = available ? 'Bonus quotidien' : 'Bonus déjà réclamé';
    dailySub.textContent = available
      ? `+${formatChips(DAILY_GRANT)} jetons`
      : `Prochain dans ${formatDuration(remaining)}`;
  }

  async function claimDaily(): Promise<void> {
    if (claiming) return;
    claiming = true;
    dailyButton.disabled = true;
    dailyLabel.textContent = 'Réclamation…';
    dailySub.textContent = 'Le serveur décide.';

    try {
      const response = await apiCall('/api/daily', {});
      // Le solde n'est pas incremente ici : on applique l'etat renvoye.
      applyUserState(response.user);

      if (response.granted > 0) {
        audio.play('win');
        toast(`+${formatChips(response.granted)} jetons crédités.`, {
          kind: 'success',
          sound: false,
        });
        celebrate(dailyButton);
      } else {
        toast('Bonus déjà réclamé pour aujourd’hui.', { kind: 'info' });
      }
    } catch (error) {
      toast(error instanceof ApiError ? error.userMessage : 'Bonus indisponible.', { kind: 'error' });
    } finally {
      claiming = false;
      refreshDaily();
    }
  }

  dailyButton.addEventListener('click', () => void claimDaily());

  // --- actions secondaires ------------------------------------------------
  const fairnessButton = el(
    'button',
    {
      class: 'button button--ghost',
      attrs: { type: 'button' },
      on: {
        click: () => {
          audio.play('click');
          openFairness();
        },
      },
    },
    [icon(ICONS.shield, { size: 17 }), 'Équité'],
  );

  const settingsButton = el(
    'button',
    {
      class: 'button button--ghost',
      attrs: { type: 'button' },
      on: {
        click: () => {
          audio.play('click');
          openSettings();
        },
      },
    },
    [icon(ICONS.settings, { size: 17 }), 'Paramètres'],
  );

  const grid = el('div', { class: 'game-grid' });
  GAME_IDS.forEach((id, index) => {
    grid.appendChild(gameCardNode(GAMES[id], index, options));
  });

  const openCount = GAME_IDS.filter((id) => GAMES[id].open).length;

  const title = el('div', { class: 'masthead' }, [
    el('p', { class: 'masthead__kicker', text: 'Salon privé · jetons virtuels' }),
    el('h1', { class: 'masthead__title' }, [
      el('span', { class: 'masthead__title-main', text: 'Salon' }),
      el('span', { class: 'masthead__title-accent', text: 'Minuit' }),
    ]),
    el('p', {
      class: 'masthead__lead',
      text: 'Quatre tables, une monnaie sans valeur, et des résultats vérifiables. Rien à acheter, rien à encaisser.',
    }),
  ]);

  const root = el('main', { class: 'screen screen--menu' }, [
    hud.root,
    el('div', { class: 'menu-body' }, [
      el('section', { class: 'menu-lead' }, [title, dailyButton]),
      el('section', { class: 'menu-tables' }, [
        el('div', { class: 'section-head' }, [
          el('h2', { class: 'section-head__title', text: 'Les tables' }),
          el('span', {
            class: 'section-head__note',
            text:
              openCount === 0
                ? 'Ouverture prochaine'
                : `${openCount} table${openCount > 1 ? 's' : ''} sur ${GAME_IDS.length}`,
          }),
        ]),
        grid,
      ]),
      el('nav', { class: 'menu-actions', attrs: { 'aria-label': 'Autres écrans' } }, [
        fairnessButton,
        settingsButton,
      ]),
      el('p', { class: 'legal legal--footer' }, [
        'Jeu social en monnaie fictive. Les jetons n’ont aucune valeur, ne peuvent être ',
        'ni achetés ni convertis, et le solde ne représente pas un avoir.',
      ]),
    ]),
  ]);

  enter(title, 0);
  enter(dailyButton, 1);

  const unsubscribe = store.subscribe(() => refreshDaily());
  const ticker = window.setInterval(() => refreshDaily(), 1000);
  refreshDaily();

  return {
    root,
    destroy: () => {
      window.clearInterval(ticker);
      unsubscribe();
      hud.destroy();
    },
  };
}

/**
 * Petite gerbe de jetons a la reception du bonus.
 * Purement decorative et posterieure a la reponse serveur : elle celebre un
 * gain deja acquis, elle n'en annonce ni n'en suggere aucun.
 */
function celebrate(anchor: HTMLElement): void {
  if (isReducedMotion()) return;
  const burst = el('div', { class: 'burst', attrs: { 'aria-hidden': 'true' } });
  anchor.appendChild(burst);

  for (let i = 0; i < 10; i += 1) {
    const chip = el('span', { class: 'burst__chip' });
    burst.appendChild(chip);
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.5;
    const distance = 46 + Math.random() * 54;
    chip.animate(
      [
        { transform: 'translate(0, 0) scale(0.5)', opacity: 0 },
        { transform: 'translate(0, 0) scale(1)', opacity: 1, offset: 0.18 },
        {
          transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(0.7)`,
          opacity: 0,
        },
      ],
      { duration: 620 + Math.random() * 240, easing: EASING.out, delay: i * 26, fill: 'forwards' },
    );
  }

  window.setTimeout(() => burst.remove(), 1200);
}
